import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getApiUrl } from "../api";
import { getStoredToken } from "../auth";

const emptyProfile = {
  ImagePath: "",
  fullname: "",
  gender: "",
  dob: "",
  nationality: "",
  maritalstatus: "",
  eidnumber: "",
  eidexpdate: "",
  passportno: "",
  passportexpdate: "",
  visatype: "",
  visaexpdate: "",
  residencystatus: "",
  mobileno: "",
  email: "",
  address: "",
  location: "",
  emirates: "",
  employmentstatus: "",
  employername: "",
  jobtitle: "",
  monthlysalary: "",
  housingtype: "",
  rentamount: "",
  ownerstatus: "",
};

const emptyLookups = {
  genders: [],
  nationalities: [],
  maritalStatuses: [],
  visaTypes: [],
  residencyStatuses: [],
  emirates: [],
  employmentStatuses: [],
  ownershipStatuses: [],
};

const historyFieldLabels = {
  fullname: ["Full Name", "الاسم الكامل"], gender: ["Gender", "الجنس"], dob: ["Date of Birth", "تاريخ الميلاد"],
  nationality: ["Nationality", "الجنسية"], maritalstatus: ["Marital Status", "الحالة الاجتماعية"],
  eidnumber: ["Emirates ID Number", "رقم الهوية الإماراتية"], eidexpdate: ["Emirates ID Expiry", "تاريخ انتهاء الهوية الإماراتية"],
  passportno: ["Passport Number", "رقم جواز السفر"], passportexpdate: ["Passport Expiry", "تاريخ انتهاء جواز السفر"],
  visatype: ["Visa Type", "نوع التأشيرة"], visaexpdate: ["Visa Expiry", "تاريخ انتهاء التأشيرة"],
  residencystatus: ["Residency Status", "حالة الإقامة"], mobileno: ["Mobile Number", "رقم الهاتف المتحرك"],
  email: ["Email", "البريد الإلكتروني"], address: ["Address", "العنوان"], location: ["City / Location", "المدينة / الموقع"],
  emirates: ["Emirate", "الإمارة"], employmentstatus: ["Employment Status", "حالة العمل"],
  employername: ["Employer Name", "اسم جهة العمل"], jobtitle: ["Job Title", "المسمى الوظيفي"],
  monthlysalary: ["Monthly Salary", "الراتب الشهري"], housingtype: ["Housing Type", "نوع السكن"],
  rentamount: ["Monthly Rental Amount", "قيمة الإيجار الشهري"], ownerstatus: ["Ownership Status", "حالة الملكية"],
};

const historyLookupMap = {
  gender: "genders", nationality: "nationalities", maritalstatus: "maritalStatuses", visatype: "visaTypes",
  residencystatus: "residencyStatuses", emirates: "emirates", employmentstatus: "employmentStatuses", ownerstatus: "ownershipStatuses",
  maritalStatus: "maritalStatuses", visaType: "visaTypes", residencyStatus: "residencyStatuses",
  emirate: "emirates", employmentStatus: "employmentStatuses", ownerStatus: "ownershipStatuses",
};

function Field({ label, children, fullWidth = false }) {
  return (
    <label className={`bn-profile-card${fullWidth ? " bn-full-width" : ""}`}>
      <span>{label}</span>
      {children}
    </label>
  );
}

function LookupSelect({ value, onChange, options, placeholder, disabled = false, isArabic = false, required = false }) {
  return (
    <select value={value || ""} onChange={onChange} disabled={disabled} required={required}>
      <option value="">{placeholder}</option>
      {(options || []).map((option) => (
        <option key={option.id} value={String(option.id)}>
          {(isArabic ? option.title_ar : null) || option.title_en || option.title}
        </option>
      ))}
    </select>
  );
}

function Section({ icon, title, children }) {
  return (
    <div className="bn-profile-section">
      <div className="bn-profile-title">
        <div className="bn-profile-title-icon">
          <i className={icon}></i>
        </div>
        <h4>{title}</h4>
      </div>
      <div className="bn-profile-grid">{children}</div>
    </div>
  );
}

export default function BeneficiaryProfileForm({ language = "en", section = "all", requireAll = false, onSaved }) {
  const isArabic = language === "ar";
  const t = (english, arabic) => isArabic ? arabic : english;
  const formRef = useRef(null);
  const [profile, setProfile] = useState(emptyProfile);
  const [lookups, setLookups] = useState(emptyLookups);
  const [profileExists, setProfileExists] = useState(false);
  const [lockedFields, setLockedFields] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");

  const requestHeaders = () => {
    const token = getStoredToken();
    return {
      Accept: "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const applyResponse = (result) => {
    setProfile({ ...emptyProfile, ...(result.profile || {}) });
    setLookups({ ...emptyLookups, ...(result.lookups || {}) });
    setProfileExists(Boolean(result.exists));
    setLockedFields(new Set(result.lockedFields || []));
  };

  const isLocked = (field) => lockedFields.has(field);

  const historyValue = (field, value) => {
    if (value === null || value === undefined || String(value).trim() === "") return t("Not provided", "غير متوفر");
    const lookupName = historyLookupMap[field];
    const option = lookupName ? (lookups[lookupName] || []).find((item) => String(item.id) === String(value)) : null;
    return option ? ((isArabic ? option.title_ar : null) || option.title_en || option.title || value) : String(value);
  };

  const departmentLabel = (value) => {
    const formatted = String(value || "TARAHUM").trim().toLowerCase().split("_")
      .filter(Boolean).map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(" ");
    return formatted === "Tarahum" ? t("Tarahum Department", "قسم تراحم") : formatted;
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    setHistoryLoading(true);
    setHistoryError("");
    try {
      const response = await fetch(getApiUrl("/api/BeneficiaryProfile.ashx?view=history"), {
        method: "GET", credentials: "include", cache: "no-store", headers: requestHeaders(),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) throw new Error(result.message || "Unable to load update history.");
      setHistory(Array.isArray(result.history) ? result.history : []);
    } catch (error) {
      setHistoryError(error.message || t("Unable to load update history.", "تعذر تحميل سجل التحديثات."));
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();

    const loadProfile = async () => {
      setIsLoading(true);
      setMessage("");
      try {
        const response = await fetch(getApiUrl("/api/BeneficiaryProfile.ashx"), {
          method: "GET",
          credentials: "include",
          headers: requestHeaders(),
          signal: controller.signal,
        });
        const result = await response.json().catch(() => ({}));
        if (!response.ok || !result.success) {
          throw new Error(result.message || "Unable to load beneficiary profile.");
        }
        applyResponse(result);
      } catch (error) {
        if (error.name !== "AbortError") {
          setIsError(true);
          setMessage(error.message || "Unable to load beneficiary profile.");
        }
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    };

    loadProfile();
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!historyOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previousOverflow; };
  }, [historyOpen]);

  const updateField = (field) => (event) => {
    setProfile((current) => ({ ...current, [field]: event.target.value }));
  };

  const saveProfile = async (event) => {
    event.preventDefault();
    setIsSaving(true);
    setIsError(false);
    setMessage("");

    try {
      const response = await fetch(getApiUrl("/api/BeneficiaryProfile.ashx"), {
        method: "POST",
        credentials: "include",
        headers: {
          ...requestHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(profile),
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) {
        throw new Error(result.message || "Unable to save beneficiary profile.");
      }

      applyResponse(result);
      setMessage(result.message || "Beneficiary profile saved successfully.");
      window.dispatchEvent(new CustomEvent("beneficiary-profile:changed", {
        detail: { profile: result.profile || {} },
      }));
      onSaved?.({ profile: result.profile || profile, lookups: result.lookups || lookups, exists: true });
      window.requestAnimationFrame(() => {
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } catch (error) {
      setIsError(true);
      setMessage(error.message || "Unable to save beneficiary profile.");
      window.requestAnimationFrame(() => {
        formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <div className="content-card"><p>{t("Loading beneficiary profile...", "جارٍ تحميل ملف المستفيد...")}</p></div>;
  }

  return (
    <form ref={formRef} className="content-card" onSubmit={saveProfile}>
      <div className="card-title beneficiary-profile-heading">
        <div>
          <h2>{t("Beneficiary Profile", "ملف المستفيد")}</h2>
          <p>{profileExists ? t("Update your saved beneficiary information.", "حدّث معلومات المستفيد المحفوظة.") : t("Complete your beneficiary information.", "أكمل معلومات المستفيد.")}</p>
        </div>
        <button className="beneficiary-history-link" type="button" onClick={openHistory}>
          <i className="fa-solid fa-clock-rotate-left" aria-hidden="true"></i>
          {t("Update History", "سجل التحديثات")}
        </button>
      </div>

      {message ? (
        <div className={`login-mdn-message${isError ? " error" : " success"}`} role="status">
          {message}
        </div>
      ) : null}

      <div className="bn-profile-wrap">
        <div className="bn-profile-box">
          {section !== "housing" ? <>
          <Section icon="fa-solid fa-user" title={t("Personal Information", "المعلومات الشخصية")}>
            <Field label={t("Full Name", "الاسم الكامل")}><input type="text" value={profile.fullname} onChange={updateField("fullname")} disabled={isLocked("fullname")} required /></Field>
            <Field label={t("Gender", "الجنس")}><LookupSelect value={profile.gender} onChange={updateField("gender")} options={lookups.genders} placeholder={t("Select gender", "اختر الجنس")} disabled={isLocked("gender")} isArabic={isArabic} required={requireAll} /></Field>
            <Field label={t("Date of Birth", "تاريخ الميلاد")}><input type="date" value={profile.dob} onChange={updateField("dob")} disabled={isLocked("dob")} required={requireAll} /></Field>
            <Field label={t("Nationality", "الجنسية")}><LookupSelect value={profile.nationality} onChange={updateField("nationality")} options={lookups.nationalities} placeholder={t("Select nationality", "اختر الجنسية")} disabled={isLocked("nationality")} isArabic={isArabic} required={requireAll} /></Field>
            <Field label={t("Marital Status", "الحالة الاجتماعية")}><LookupSelect value={profile.maritalstatus} onChange={updateField("maritalstatus")} options={lookups.maritalStatuses} placeholder={t("Select marital status", "اختر الحالة الاجتماعية")} disabled={isLocked("maritalstatus")} isArabic={isArabic} required={requireAll} /></Field>
          </Section>

          <Section icon="fa-solid fa-id-card" title={t("Identity Information", "معلومات الهوية")}>
            <Field label={t("Emirates ID Number", "رقم الهوية الإماراتية")}><input type="text" value={profile.eidnumber} onChange={updateField("eidnumber")} maxLength="18" disabled={isLocked("eidnumber")} required={requireAll} /></Field>
            <Field label={t("Emirates ID Expiry", "تاريخ انتهاء الهوية الإماراتية")}><input type="date" value={profile.eidexpdate} onChange={updateField("eidexpdate")} required={requireAll} /></Field>
            <Field label={t("Passport Number", "رقم جواز السفر")}><input type="text" value={profile.passportno} onChange={updateField("passportno")} required={requireAll} /></Field>
            <Field label={t("Passport Expiry", "تاريخ انتهاء جواز السفر")}><input type="date" value={profile.passportexpdate} onChange={updateField("passportexpdate")} required={requireAll} /></Field>
          </Section>

          <Section icon="fa-solid fa-passport" title={t("Residency Information", "معلومات الإقامة")}>
            <Field label={t("Visa Type", "نوع التأشيرة")}><LookupSelect value={profile.visatype} onChange={updateField("visatype")} options={lookups.visaTypes} placeholder={t("Select visa type", "اختر نوع التأشيرة")} isArabic={isArabic} required={requireAll} /></Field>
            <Field label={t("Visa Expiry", "تاريخ انتهاء التأشيرة")}><input type="date" value={profile.visaexpdate} onChange={updateField("visaexpdate")} required={requireAll} /></Field>
            <Field label={t("Residency Status", "حالة الإقامة")}><LookupSelect value={profile.residencystatus} onChange={updateField("residencystatus")} options={lookups.residencyStatuses} placeholder={t("Select residency status", "اختر حالة الإقامة")} isArabic={isArabic} required={requireAll} /></Field>
          </Section>

          <Section icon="fa-solid fa-phone" title={t("Contact Information", "معلومات التواصل")}>
            <Field label={t("Mobile Number", "رقم الهاتف المتحرك")}><input type="tel" value={profile.mobileno} onChange={updateField("mobileno")} maxLength="13" disabled={isLocked("mobileno")} required={requireAll} /></Field>
            <Field label={t("Email", "البريد الإلكتروني")}><input type="email" value={profile.email} onChange={updateField("email")} disabled={isLocked("email")} required={requireAll} /></Field>
            <Field label={t("Address", "العنوان")}><input type="text" value={profile.address} onChange={updateField("address")} required={requireAll} /></Field>
            <Field label={t("City / Location", "المدينة / الموقع")}><input type="text" value={profile.location} onChange={updateField("location")} required={requireAll} /></Field>
            <Field label={t("Emirate", "الإمارة")}><LookupSelect value={profile.emirates} onChange={updateField("emirates")} options={lookups.emirates} placeholder={t("Select emirate", "اختر الإمارة")} isArabic={isArabic} required={requireAll} /></Field>
          </Section>

          <Section icon="fa-solid fa-briefcase" title={t("Employment Information", "معلومات العمل")}>
            <Field label={t("Employment Status", "حالة العمل")}><LookupSelect value={profile.employmentstatus} onChange={updateField("employmentstatus")} options={lookups.employmentStatuses} placeholder={t("Select employment status", "اختر حالة العمل")} isArabic={isArabic} required={requireAll} /></Field>
            <Field label={t("Employer Name", "اسم جهة العمل")}><input type="text" value={profile.employername} onChange={updateField("employername")} required={requireAll} /></Field>
            <Field label={t("Job Title", "المسمى الوظيفي")}><input type="text" value={profile.jobtitle} onChange={updateField("jobtitle")} required={requireAll} /></Field>
            <Field label={t("Monthly Salary", "الراتب الشهري")}><input type="number" min="0" step="1" value={profile.monthlysalary} onChange={updateField("monthlysalary")} placeholder="AED" required={requireAll} /></Field>
          </Section>
          </> : null}

          {section !== "profile" ? <Section icon="fa-solid fa-house" title={t("Housing Information", "معلومات السكن")}>
            <Field label={t("Housing Type", "نوع السكن")}><input type="text" value={profile.housingtype} onChange={updateField("housingtype")} placeholder={t("Apartment, villa, shared housing...", "شقة، فيلا، سكن مشترك...")} required={requireAll} /></Field>
            <Field label={t("Monthly Rental Amount", "قيمة الإيجار الشهري")}><input type="number" min="0" step="1" value={profile.rentamount} onChange={updateField("rentamount")} placeholder="AED" required={requireAll} /></Field>
            <Field label={t("Ownership Status", "حالة الملكية")}><LookupSelect value={profile.ownerstatus} onChange={updateField("ownerstatus")} options={lookups.ownershipStatuses} placeholder={t("Select ownership status", "اختر حالة الملكية")} isArabic={isArabic} required={requireAll} /></Field>
          </Section>
          : null}
        </div>
      </div>

      <button className="submit-btn" type="submit" disabled={isSaving}>
        {isSaving ? t("Saving...", "جارٍ الحفظ...") : profileExists ? t("Update Profile", "تحديث الملف الشخصي") : t("Save Profile", "حفظ الملف الشخصي")}
      </button>

      {historyOpen && typeof document !== "undefined" ? createPortal((
        <div className="beneficiary-history-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setHistoryOpen(false); }}>
          <section className="beneficiary-history-modal" role="dialog" aria-modal="true" aria-labelledby="beneficiaryHistoryTitle">
            <header>
              <div>
                <span>{t("Beneficiary profile", "ملف المستفيد")}</span>
                <h2 id="beneficiaryHistoryTitle">{t("Update History", "سجل التحديثات")}</h2>
                <p>{history.length ? t(`${history.length} recorded field ${history.length === 1 ? "change" : "changes"}`, `${history.length} تغييرات مسجلة`) : t("Review changes made by you and Tarahum employees.", "راجع التغييرات التي أجريتها أنت وموظفو تراحم.")}</p>
              </div>
              <button type="button" onClick={() => setHistoryOpen(false)} aria-label={t("Close update history", "إغلاق سجل التحديثات")}>
                <i className="fa-solid fa-xmark" aria-hidden="true"></i>
              </button>
            </header>
            <div className="beneficiary-history-body">
              {historyLoading ? <div className="beneficiary-history-empty"><i className="fa-solid fa-spinner fa-spin"></i><p>{t("Loading update history...", "جارٍ تحميل سجل التحديثات...")}</p></div> : null}
              {!historyLoading && historyError ? <div className="beneficiary-history-error" role="alert">{historyError}</div> : null}
              {!historyLoading && !historyError && !history.length ? <div className="beneficiary-history-empty"><i className="fa-regular fa-clock"></i><h3>{t("No updates recorded yet", "لا توجد تحديثات مسجلة بعد")}</h3><p>{t("Future profile changes will appear here.", "ستظهر تغييرات الملف الشخصي المستقبلية هنا.")}</p></div> : null}
              {!historyLoading && !historyError && history.length ? <div className="beneficiary-history-list">
                {history.map((entry) => {
                  const employee = String(entry.actorType || "").toUpperCase() === "EMPLOYEE";
                  const actor = employee ? departmentLabel(entry.actorDepartment) : String(entry.actorType || "").toUpperCase() === "BENEFICIARY" ? t("You", "أنت") : (entry.actorName || t("System", "النظام"));
                  const fallbackLabel = historyFieldLabels[entry.field] || [entry.field, entry.field];
                  const label = entry.fieldLabel && entry.fieldLabel !== entry.field
                    ? entry.fieldLabel
                    : (isArabic ? fallbackLabel[1] : fallbackLabel[0]);
                  const changedAt = entry.changedAt ? new Date(entry.changedAt).toLocaleString(isArabic ? "ar-AE" : "en-AE", { dateStyle: "medium", timeStyle: "short" }) : "";
                  return <article className="beneficiary-history-entry" key={entry.id}>
                    <div className={`beneficiary-history-icon ${employee ? "employee" : "beneficiary"}`}><i className={`fa-solid ${employee ? "fa-user-tie" : "fa-user"}`}></i></div>
                    <div className="beneficiary-history-change">
                      <div className="beneficiary-history-meta"><div><strong>{actor}</strong>{!employee && entry.actorRole ? <small>{entry.actorRole}</small> : null}</div><time>{changedAt}</time></div>
                      <h3>{label}</h3>
                      <div className="beneficiary-history-values">
                        <span><small>{t("Previous value", "القيمة السابقة")}</small>{historyValue(entry.field, entry.oldValue)}</span>
                        <i className={`fa-solid ${isArabic ? "fa-arrow-left" : "fa-arrow-right"}`}></i>
                        <strong><small>{t("New value", "القيمة الجديدة")}</small>{historyValue(entry.field, entry.newValue)}</strong>
                      </div>
                    </div>
                  </article>;
                })}
              </div> : null}
            </div>
          </section>
        </div>
      ), document.body) : null}
    </form>
  );
}
